import React, { FC, useRef, useState } from 'react';

interface FacilityImagePickerProps {
  facilityId: number;
  /** The chosen file, held by the page until commit succeeds. */
  imageFile: File | null;
  /** An object URL for `imageFile`, owned by the page so it is revoked once. */
  imagePreviewUrl: string | null;
  onImageFileChange: (file: File | null) => void;
}

/**
 * Pick the facility overview PNG.
 *
 * Lives on the review step so the pitch centres can be marked on it, and is offered
 * again on the preview step for a reviewer who skipped it. Nothing is uploaded before
 * commit: the file stays in memory and goes to the bucket only once the facility exists.
 */
export const FacilityImagePicker: FC<FacilityImagePickerProps> = ({
  facilityId,
  imageFile,
  imagePreviewUrl,
  onImageFileChange,
}) => {
  const [pickError, setPickError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const imageFileName = `facility${facilityId}.png`;

  const pickImage = (file: File | undefined): void => {
    if (!file) {
      return;
    }
    // stored as facility<id>.png, so only a real PNG can go in the bucket
    if (file.type !== 'image/png') {
      setPickError('The overview image must be a PNG file.');
      return;
    }
    setPickError('');
    onImageFileChange(file);
  };

  return (
    <div className="p-4 bg-white border rounded border-primary-250">
      <p className="text-base font-semibold">Facility overview image</p>
      <p className="mt-1 text-sm text-primary-500">
        {'Uploaded to '}
        <code className="text-sm">smart-pitches-facility-images</code>
        {' as '}
        <code className="text-sm">{imageFileName}</code>
        {' when the facility is created — nothing is uploaded before then. '}
        {'Each pitch centre is marked by clicking on it below.'}
      </p>
      <input
        ref={inputRef}
        type="file"
        accept="image/png"
        className="hidden"
        onChange={event => {
          pickImage(event.target.files?.[0]);
          // allow re-picking the same file after removing it
          event.target.value = '';
        }}
      />
      {imageFile && imagePreviewUrl ? (
        <div className="flex items-center mt-3 space-x-4">
          <img
            src={imagePreviewUrl}
            alt="Facility overview preview"
            className="object-cover w-24 h-24 border rounded border-primary-250"
          />
          <div>
            <p className="text-sm">
              {imageFile.name}
              {` (${Math.max(1, Math.round(imageFile.size / 1024))} KB)`}
            </p>
            <div className="flex mt-1 space-x-3">
              <button
                type="button"
                className="text-sm font-semibold text-blue"
                onClick={() => inputRef.current?.click()}
              >
                Replace
              </button>
              <button
                type="button"
                className="text-sm font-semibold text-red"
                onClick={() => onImageFileChange(null)}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="mt-3 text-sm font-semibold text-blue"
          onClick={() => inputRef.current?.click()}
        >
          Choose PNG image…
        </button>
      )}
      {!!pickError && <p className="mt-2 text-sm text-red-dark">{pickError}</p>}
    </div>
  );
};
